import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useDecartSession } from "@/hooks/useDecartSession";
import { useObsChannel } from "@/hooks/useObsChannel";
import { useMirrorPublisher } from "@/hooks/useMirrorPublisher";
import { useStudioControls } from "@/hooks/useStudioControls";
import { useMicPassthrough } from "@/hooks/useMicPassthrough";
import { RawPanel } from "@/components/studio/RawPanel";
import { PromptPanel } from "@/components/studio/PromptPanel";
import { ReferencePanel } from "@/components/studio/ReferencePanel";
import { SessionControls } from "@/components/studio/SessionControls";
import { OutputPanel } from "@/components/studio/OutputPanel";
import { QuickStyles } from "@/components/studio/QuickStyles";
import { ObsPanel } from "@/components/studio/ObsPanel";

const ControlsColumn = ({ session, studio, isLive, isConnecting, fastMode, onFastModeChange }) => (
    <div className="lg:col-span-4 flex flex-col gap-6">
        <RawPanel
            localVideoRef={session.localVideoRef}
            isLive={isLive}
            isConnecting={isConnecting}
            orientation={studio.orientation}
        />
        <PromptPanel
            prompt={studio.prompt}
            onChange={studio.handlePromptChange}
            onSubmit={studio.submitPrompt}
            disabled={!isLive}
        />
        <ReferencePanel
            referencePreview={studio.referencePreview}
            onFileChange={studio.handleReferenceChange}
            onClear={studio.clearReference}
        />
        <SessionControls
            isLive={isLive}
            isConnecting={isConnecting}
            onStart={() => studio.handleStart({ speed: fastMode ? "fast" : "standard" })}
            onStop={session.stopSession}
            errorMessage={session.errorMessage}
            orientation={studio.orientation}
            onOrientationChange={studio.setOrientation}
            fastMode={fastMode}
            onFastModeChange={onFastModeChange}
        />
    </div>
);

const OutputColumn = ({ session, studio, obs, mic, isLive, isConnecting, isMirroring }) => (
    <div className="lg:col-span-8 flex flex-col gap-6">
        <OutputPanel
            remoteVideoRef={session.remoteVideoRef}
            isLive={isLive}
            isConnecting={isConnecting}
            hasOutput={session.hasOutput}
            statusDetail={session.statusDetail}
            orientation={studio.orientation}
        />
        <QuickStyles
            activePreset={studio.activePreset}
            referencePreview={studio.referencePreview}
            savedPresets={studio.savedPresets}
            showSaveInput={studio.showSaveInput}
            presetName={studio.presetName}
            onNone={studio.handleNonePreset}
            onPreset={studio.handlePresetClick}
            onCustomUpload={studio.handleCustomUpload}
            onSavedPreset={studio.handleSavedPresetClick}
            onDeleteSaved={studio.deleteSavedStyle}
            onToggleSaveForm={() => studio.setShowSaveInput((s) => !s)}
            onPresetNameChange={(e) => studio.setPresetName(e.target.value)}
            onSaveConfirm={studio.saveCurrentStyle}
            onSaveCancel={() => {
                studio.setShowSaveInput(false);
                studio.setPresetName("");
            }}
        />
        <ObsPanel
            obsUrl={obs.obsUrl}
            obsCopied={obs.obsCopied}
            onCopy={obs.copyObsLink}
            isLive={isMirroring}
            micOn={mic.micOn}
            onToggleMic={mic.toggleMic}
        />
    </div>
);

const WorkspaceHeader = () => (
    <div className="flex items-baseline justify-between flex-wrap gap-3 mb-8">
        <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-blaze">
                / Workspace
            </div>
            <h2 className="font-display font-bold uppercase text-3xl sm:text-4xl mt-2 tracking-tight">
                The Control Room
            </h2>
        </div>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 max-w-md">
            Permission your camera. Describe the look. SPCLIVE paints every frame.
        </p>
    </div>
);

export default function LucyMirror({ onConnectionStateChange, forceStopRef, fastMode, onFastModeChange }) {
    const { user } = useAuth();
    const session = useDecartSession(onConnectionStateChange);
    const obs = useObsChannel(user?.obs_token);
    const studio = useStudioControls(session);
    const mic = useMicPassthrough();

    const isLive = session.connectionState === "live";
    const isConnecting = session.connectionState === "connecting";
    const isReconnecting = session.connectionState === "reconnecting";

    useMirrorPublisher({
        obsToken: user?.obs_token,
        remoteVideoRef: session.remoteVideoRef,
        isLive: isLive || isReconnecting,
        micTrackRef: mic.micTrackRef,
        micOn: mic.micOn,
    });

    useEffect(() => {
        if (forceStopRef) forceStopRef.current = session.stopSession;
    }, [forceStopRef, session.stopSession]);

    return (
        <section
            id="studio"
            className="relative max-w-[1600px] mx-auto px-6 lg:px-10 py-12 lg:py-16"
            data-testid="studio-section"
        >
            <WorkspaceHeader />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <ControlsColumn
                    session={session}
                    studio={studio}
                    isLive={isLive}
                    isConnecting={isConnecting}
                    fastMode={fastMode}
                    onFastModeChange={onFastModeChange}
                />
                <OutputColumn
                    session={session}
                    studio={studio}
                    obs={obs}
                    mic={mic}
                    isLive={isLive}
                    isConnecting={isConnecting}
                    isMirroring={isLive || isReconnecting}
                />
            </div>
        </section>
    );
}
